import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  FileText,
  Edit,
  Trash2,
  Copy,
  CheckCircle,
  Loader2,
  Settings,
  Calendar,
  MoreVertical,
  Shield,
  HardHat,
  ClipboardCheck
} from 'lucide-react';
import { api } from '@/utils/api';

interface Template {
  id: number;
  name: string;
  description?: string;
  program: string;
  version: string;
  isSystemDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export function AuditTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<number | null>(null);
  // Template authoring is server-gated to ADMIN + SAFETY_MANAGER; mirror that
  // in the UI so non-admins can view templates but never edit them.
  const canEdit = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return u?.role === 'ADMIN' || u?.role === 'SAFETY_MANAGER';
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/safety-audits/templates/all');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    setDeleting(id);
    try {
      await api.delete(`/safety-audits/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template');
    } finally {
      setDeleting(null);
    }
  };

  const handleDuplicate = async (template: Template, navigateToEdit = false) => {
    try {
      const response = await api.get(`/safety-audits/templates/${template.id}`);
      const fullTemplate = response.data.data;

      const newTemplate = {
        name: `${template.name} (Copy)`,
        description: template.description,
        program: template.program,
        version: '1.0',
        elements: fullTemplate.elements,
        metadata: fullTemplate.metadata,
      };

      const created = await api.post('/safety-audits/templates', newTemplate);
      const newId = created.data?.data?.id;
      loadTemplates();
      // S4: "Customize (copy)" jumps straight into the builder on the copy.
      if (navigateToEdit && newId) navigate(`/safety-audits/templates/${newId}/edit`);
    } catch (error) {
      console.error('Error duplicating template:', error);
      alert('Failed to duplicate template');
    }
  };

  const getTemplateIcon = (program: string) => {
    if (program.toLowerCase().includes('quality')) return ClipboardCheck;
    if (program.toLowerCase().includes('safety') || program.toLowerCase().includes('h&s')) return Shield;
    return HardHat;
  };

  const getTemplateColor = (program: string) => {
    if (program.toLowerCase().includes('quality')) return 'text-blue-400 bg-blue-900/30';
    if (program.toLowerCase().includes('safety') || program.toLowerCase().includes('h&s')) return 'text-emerald-400 bg-emerald-900/30';
    return 'text-purple-400 bg-purple-900/30';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/safety-audits" className="text-slate-400 hover:text-white">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">Audit Templates</h1>
                <p className="text-sm text-slate-400">
                  Manage your audit checklists and inspection templates
                </p>
              </div>
            </div>
            {canEdit && (
              <Link
                to="/safety-audits/templates/create"
                className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create Template
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Templates Yet</h2>
            <p className="text-slate-400 mb-6">
              Create your first audit template to get started
            </p>
            <Link
              to="/safety-audits/templates/create"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700"
            >
              <Plus className="h-5 w-5" />
              Create Template
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => {
              const IconComponent = getTemplateIcon(template.program);
              const colorClass = getTemplateColor(template.program);
              
              return (
                <div
                  key={template.id}
                  className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 hover:border-slate-600 transition"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${colorClass}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                    <div className="flex items-center gap-1">
                        {template.isSystemDefault && (
                          <span className="px-2 py-1 bg-amber-900/30 text-amber-400 text-xs rounded-full mr-1">
                            System Default
                          </span>
                        )}
                        {canEdit && (
                          <>
                            {template.isSystemDefault ? (
                              // S4: system defaults are read-only — the pencil
                              // becomes "Customize" which duplicates then jumps
                              // straight into editing the copy.
                              <button
                                onClick={() => void handleDuplicate(template, true)}
                                className="p-1.5 text-amber-400 hover:text-amber-300 rounded"
                                title="Customize (copy)"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => navigate(`/safety-audits/templates/${template.id}/edit`)}
                                className="p-1.5 text-slate-400 hover:text-blue-400 rounded"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => void handleDuplicate(template)}
                              className="p-1.5 text-slate-400 hover:text-purple-400 rounded"
                              title="Duplicate"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {canEdit && !template.isSystemDefault && (
                          <button
                            onClick={() => handleDelete(template.id)}
                            disabled={deleting === template.id}
                            className="p-1.5 text-slate-400 hover:text-red-400 rounded disabled:opacity-50"
                            title="Delete"
                          >
                            {deleting === template.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-1">
                    {template.name}
                  </h3>
                  <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                    {template.description || template.program}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Settings className="h-3 w-3" />
                      v{template.version}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(template.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => navigate(`/safety-audits/create?templateId=${template.id}`)}
                    className="w-full mt-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition text-sm"
                  >
                    Use This Template
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

